import { ParameterSelect } from "@/components/atoms/ParameterSelect";
import { useGetTable } from "@/features/crm_system/hooks/useQuery/airtable/useGetTable/useGetTable";
import { airtableDefaultConfigurationElements } from "@/lib/utils/crm/airtable/defaultConfiguration";
import { getAirtableMetaDataFromConfiguration } from "@/lib/utils/crm/airtable/getAirtableMetaDataFromConfiguration";
import { ParameterData } from "@/types/crm/configuration/parameter";
import { useEffect, useState } from "react";
import { ConfigurationParametersInputProps, FallbackConfigurationParameterInput, ParameterInputProps } from "../../_common/ConfigurationParameterInput";
import { useGetTablesList } from "@/features/crm_system/hooks/useQuery/airtable/useGetTablesList";
import { useGetBasesForApiKey } from "@/features/crm_system/hooks/useQuery/airtable/useGetBasesForApiKey";
import { Spinner } from "@/components/ui/spinner";
import { BorderBox } from "@/components/atoms/BorderBox";
import { crmRegistry } from "../../../_core/crmRegistry";
import { normalizeAirtableFields } from "../__utils/normalizeAirtableFieldTypesIntoQuestions";

export function AirtableConfigurationParameterInput({ parameters, values, onChange, fullConfiguration, onEvent }: Readonly<ConfigurationParametersInputProps>) {
    console.log('AirtableConfigurationParameterInput render')
    return (
        <>
            {parameters.map((parameter, i) => {
                const baseIdParameterIndex = parameters.findIndex((parameter) => parameter.name === airtableDefaultConfigurationElements.parameter.project.baseId);
                const baseIdParameter = baseIdParameterIndex === -1 ? undefined : parameters[baseIdParameterIndex];
                if (baseIdParameter) {
                    return (
                        <AirtableBaseIdParameterInput
                            key={parameter.id}
                            parameter={parameter}
                            value={values[i]}
                            onChange={(value) => onChange(i, value)}
                            fullConfiguration={fullConfiguration}
                            onEvent={onEvent}
                        />
                    )
                }
                const tableNameParameterIndex = parameters.findIndex((parameter) => parameter.name === airtableDefaultConfigurationElements.parameter.survey.tableName);
                const tableNameParameter = tableNameParameterIndex === -1 ? undefined : parameters[tableNameParameterIndex];
                if (tableNameParameter) {
                    return (
                        <AirtableTableNameParameterInput
                            key={parameter.id}
                            parameter={parameter}
                            value={values[i]}
                            onChange={(value) => onChange(i, value)}
                            fullConfiguration={fullConfiguration}
                            onEvent={onEvent}
                        />
                    )
                }
                const columnNameParameterIndex = parameters.findIndex((parameter) => parameter.name === airtableDefaultConfigurationElements.parameter.question.columnName);
                const columnNameParameter = columnNameParameterIndex === -1 ? undefined : parameters[columnNameParameterIndex];
                if (columnNameParameter) {
                    return (
                        <AirtableColumnNameParameterInput
                            key={parameter.id}
                            parameter={parameter}
                            value={values[i]}
                            onChange={(value) => onChange(i, value)}
                            fullConfiguration={fullConfiguration}
                            onEvent={onEvent}
                        />
                    )
                }
                return (
                    <FallbackConfigurationParameterInput
                        key={parameter.id}
                        parameter={parameter}
                        value={values[i]}
                        onChange={(value) => onChange(i, value)}
                        fullConfiguration={fullConfiguration}
                    />
                );
            })}
        </>
    );
}



function AirtableBaseIdParameterInput({ parameter, value, onChange, fullConfiguration }: Readonly<ParameterInputProps>) {
    const { apiKey } = getAirtableMetaDataFromConfiguration(fullConfiguration);
    const { data: basesData, isLoading: isLoadingBasesData, error: basesDataError } = useGetBasesForApiKey(apiKey ?? undefined, true);
    console.log(basesData)
    const [basesParameterOptions, setBasesParameterOptions] = useState<ParameterData[] | undefined>()

    useEffect(() => {
        if (basesData && !('bases' in basesData)) return;
        setBasesParameterOptions(basesData?.bases?.map((base) => {
            return { key: `${base.name} (${base.id})`, value: base.id }
        }))
    }, [basesData])
    if (basesDataError) {
        return <div className="text-sm text-red-500">Error loading Airtable Bases Data: {basesDataError.message}</div>;
    }
    if (isLoadingBasesData) {
        return <div className="flex gap-2 text-sm text-muted-foreground "> <Spinner className='size-4' />Loading Airtable Data...</div>;
    }
    return (
        <div key={parameter.id} className="space-y-2">
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                {parameter.name}
            </label>
            {apiKey === undefined ? (
                <BorderBox variant='info' size='sm'>
                    Autocompletion options for this parameter will be available once you fill in the Personal Access Token .
                </BorderBox>
            ) : null}
            <ParameterSelect
                parameterData={{ ...parameter, options: basesParameterOptions ?? [] }}
                value={value ?? undefined}
                onChange={onChange}
                onCreate={(value) => {
                    setBasesParameterOptions((prev) => [...(prev ?? []), { key: value, value }]);
                    onChange(value ?? undefined);
                }}
            />
        </div>
    )
}
function AirtableTableNameParameterInput({ parameter, value, onChange, fullConfiguration }: Readonly<ParameterInputProps>) {
    const { apiKey, baseId } = getAirtableMetaDataFromConfiguration(fullConfiguration);
    console.log('apiKey, baseId')
    console.log(apiKey, baseId)
    const { data: tablesData, isLoading: isLoadingTablesData, error: tableDataError } = useGetTablesList({
        apiKey: apiKey ?? undefined,
        baseId: baseId ?? undefined,
    })
    console.log(tablesData)
    const [tableNameParameterOptions, setTableNameParameterOptions] = useState<ParameterData[] | undefined>()

    useEffect(() => {
        setTableNameParameterOptions(tablesData?.tables?.map((table) => {
            return { key: table.name, value: table.name }
        }))
    }, [tablesData])
    if (tableDataError) {
        return <div className="text-sm text-red-500">Error loading Airtable Table Data: {tableDataError.message}</div>;
    }
    if (isLoadingTablesData) {
        return <div className="flex gap-2 text-sm text-muted-foreground "> <Spinner className='size-4' />Loading Airtable Data...</div>;
    }
    return (
        <div key={parameter.id} className="space-y-2">
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                {parameter.name}
            </label>
            {baseId === undefined || apiKey === undefined ? (
                <BorderBox variant='info' size='sm'>
                    Autocompletion options for this parameter will be available once you fill in the Personal Access Token  and Base ID.
                </BorderBox>
            ) : null}
            <ParameterSelect
                parameterData={{ ...parameter, options: tableNameParameterOptions ?? [] }}
                value={value ?? undefined}
                onChange={onChange}
                onCreate={(value) => {
                    setTableNameParameterOptions((prev) => [...(prev ?? []), { key: value, value }]);
                    onChange(value ?? undefined);
                }}
            />
        </div>
    )
}
function AirtableColumnNameParameterInput({ parameter, value, onChange, fullConfiguration, onEvent }: Readonly<ParameterInputProps>) {
    const { apiKey, baseId, tableNameOrId } = getAirtableMetaDataFromConfiguration(fullConfiguration);
    console.log(fullConfiguration)
    console.log(apiKey, baseId, tableNameOrId)
    const { data: tableData, isLoading: isLoadingTablesData, error: tableDataError } = useGetTable({
        apiKey: apiKey ?? undefined,
        baseId: baseId ?? undefined,
        tableNameOrId: tableNameOrId as string
    })
    console.log(tableData)
    const [columnNameParameterOptions, setColumnNameParameterOptions] = useState<ParameterData[] | undefined>()
    const crmSystemConfiguration = crmRegistry.getSystemById('airtable')
    const unsupportedFieldTypesFoundInTable = tableData?.fields?.filter(field => crmSystemConfiguration?.notSupportedFieldTypes?.includes(field.type)) ?? [];
    useEffect(() => {
        setColumnNameParameterOptions(tableData?.fields?.filter(field => !crmSystemConfiguration?.notSupportedFieldTypes?.includes(field.type)).map((field) => {
            return { key: field.name, value: field.name }
        }))
    }, [tableData, crmSystemConfiguration])
    if (tableDataError) {
        return <div className="text-sm text-red-500">Error loading Airtable Table Data: {tableDataError.message}</div>;
    }
    if (isLoadingTablesData) {
        return <div className="flex gap-2 text-sm text-muted-foreground "> <Spinner className='size-4' />Loading Airtable Data...</div>;
    }
    return (
        <div key={parameter.id} className="space-y-2">
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                {parameter.name}
            </label>
            {baseId === undefined || tableNameOrId === undefined || apiKey === undefined ? (
                <BorderBox variant='info' size='sm'>
                    Autocompletion options for this parameter will be available once you fill in the Personal Access Token , Base ID and Table Name parameters.
                </BorderBox>
            ) : null}
            {unsupportedFieldTypesFoundInTable.length > 0 ? (
                <BorderBox variant='danger' size='sm'>
                    The following columns are of type not yet supported by the system: {unsupportedFieldTypesFoundInTable.map(f => f.name).join(', ')}
                </BorderBox>
            ) : null}
            <ParameterSelect
                parameterData={{ ...parameter, options: columnNameParameterOptions ?? [] }}
                value={value ?? undefined}
                onChange={(value) => {
                    onChange(value);
                    if (!value) {
                        onEvent?.({ type: 'fieldTypeReset' });
                        return;
                    }
                    const field = tableData?.fields?.find(f => f.name === value);
                    if (field?.type) onEvent?.({ type: 'fieldTypeResolved', question: normalizeAirtableFields(field, {survey_id: 'temp'})});
                }}
                onCreate={(value) => {
                    setColumnNameParameterOptions((prev) => [...(prev ?? []), { key: value, value }]);
                    onChange(value ?? undefined);
                    onEvent?.({ type: 'fieldTypeReset' });
                }}
            />
        </div>
    )
}